import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { getTailwindColor } from '@trqk/utils';

import { unstyleScrollbar } from '../helpers';

interface Props {
    chartDivId: string;
    height: number;
    chartTrackPerGenreBucket: ChartTrackPerGenreBucket[];
}

const xAxesCategory = 'work_title';
const yAxesValue = 'earnings';
const valueAxisLabelColor = getTailwindColor('tertiary-400');
const zoomLevel = 10;

/**
 * A bar chart showing earnings per track with compact configuration: category axis labels are
 * angled at 45 degrees, font is small, chart height is reduced.
 */
export class EarningsPerTrackBar extends Component<Props> {
    private chart: am4charts.XYChart | null = null;

    componentDidMount(): void {
        const { chartDivId, chartTrackPerGenreBucket } = this.props;

        const chart = am4core.create(chartDivId, am4charts.XYChart);

        chart.data = chartTrackPerGenreBucket;
        chart.numberFormatter.numberFormat = '$#,###.00##';
        chart.fontSize = '0.8rem';

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());

        categoryAxis.dataFields.category = xAxesCategory;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.labels.template.fullWords = false;
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.maxWidth = 120;
        categoryAxis.renderer.labels.template.rotation = -45;
        // eslint-disable-next-line no-template-curly-in-string
        categoryAxis.renderer.labels.template.tooltipText = '{category}';
        categoryAxis.renderer.labels.template.tooltipX = am4core.percent(100);
        categoryAxis.renderer.labels.template.truncate = true;
        categoryAxis.renderer.labels.template.verticalCenter = 'top';
        // Ensures that the first category label is shown
        categoryAxis.renderer.minGridDistance = 0.01;

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

        valueAxis.min = 0;
        valueAxis.renderer.labels.template.fill =
            am4core.color(valueAxisLabelColor);

        // Set up scroll bars and zoom level per initial data array length.
        // If the array length changes, this solution will not work, as it runs only on initial
        // mount and refers to the length at that point.
        categoryAxis.maxZoomCount = zoomLevel;
        chart.zoomOutButton.disabled = true;

        if (chart.data.length <= zoomLevel) {
            categoryAxis.endLocation = 1 * (zoomLevel + 1 - chart.data.length);
        } else {
            chart.events.on('ready', (): void => {
                categoryAxis.zoomToIndexes(0, zoomLevel - 1);
            });
            chart.scrollbarX = new am4core.Scrollbar();
            unstyleScrollbar(chart.scrollbarX);
        }

        this.chart = chart;
        this.createSeries();
    }

    componentDidUpdate(prevProps: Props): void {
        const { chartTrackPerGenreBucket } = this.props;
        const { chart } = this;

        if (
            prevProps.chartTrackPerGenreBucket !== chartTrackPerGenreBucket &&
            chart !== null
        ) {
            chart.data = chartTrackPerGenreBucket;
        }
    }

    componentWillUnmount(): void {
        const { chart } = this;

        if (chart) {
            chart.dispose();
        }
    }

    createSeries(): am4charts.ColumnSeries | void {
        const { chart } = this;

        if (chart !== null) {
            const columnSeries = chart.series.push(
                new am4charts.ColumnSeries(),
            );

            columnSeries.name = 'earnings';
            columnSeries.dataFields.valueY = yAxesValue;
            columnSeries.dataFields.categoryX = xAxesCategory;
            columnSeries.sequencedInterpolation = true;
            columnSeries.columns.template.width = am4core.percent(80);
            columnSeries.columns.template.tooltipText =
                // eslint-disable-next-line no-template-curly-in-string
                '{categoryX}: {valueY}';

            return columnSeries;
        }

        return undefined;
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;

        return <div className="w-full" style={{ height }} id={chartDivId} />;
    }
}
